// Utility helpers extracted from processMiner.js.
// IMPORTANT: These functions are intentionally dependency-free (no references to processMiner globals)
// so they can be reused safely across the UI codebase.
// Optional: validate base64 format first
export function _base64ToArrayBuffer(base64, source) {
  const label = source || 'unknown';
  if (!base64 || typeof base64 !== 'string') {
    throw new Error(`[${label}] base64 input is missing or not a string (got: ${typeof base64})`);
  }
  try {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (e) {
    // Always throw — returning null causes a cryptic pako "incorrect header check" downstream
    throw new Error(`[${label}] base64 decode failed: ${e?.message || e}`);
  }
}
export function wait() {
  return new Promise((resolve) => setTimeout(() => resolve()));
}
export function splitStringByByteLength(str, byteLength) {
  // Browser-safe UTF-8 chunking (Forge Custom UI runs in the browser; Node Buffer may not exist).
  if (str === null || str === undefined) str = '';
  if (typeof str !== 'string') str = String(str);
  const encoder = new TextEncoder();
  const decoder = new TextDecoder('utf-8');
  const bytes = encoder.encode(str);
  const result = [];
  let currentByteIndex = 0;
  while (currentByteIndex < bytes.length) {
    let nextByteIndex = currentByteIndex + byteLength;
    if (nextByteIndex > bytes.length) nextByteIndex = bytes.length;
    // Ensure we don't split in the middle of a multi-byte character.
    // UTF-8 continuation bytes have the form 10xxxxxx (0x80..0xBF).
    while (
      nextByteIndex > currentByteIndex &&
      nextByteIndex < bytes.length &&
      (bytes[nextByteIndex] & 0xc0) === 0x80
    ) {
      nextByteIndex--;
    }
    // If the entire chunk is a continuation of a character, move forward.
    if (nextByteIndex == currentByteIndex) {
      nextByteIndex = Math.min(currentByteIndex + 1, bytes.length);
    }
    const chunk = bytes.slice(currentByteIndex, nextByteIndex);
    result.push(decoder.decode(chunk));
    currentByteIndex = nextByteIndex;
  }
  return result;
}
export function convertCSVtoArray(csv, skipCaretNormalize = false) {
  if (!csv || csv === '') return [];
  // Normalize line endings
  let normalized = String(csv).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  // Normalize legacy double-caret delimiter to single caret.
  // Skip for SNOW where ^^ means two consecutive empty fields, not a multi-value separator.
  if (!skipCaretNormalize && normalized.includes('^^')) normalized = normalized.replace(/\^\^/g, '^');
  const tempRows = normalized.split('\n').filter((r) => r != null && r !== '');
  if (tempRows.length === 0) return [];
  const DEFAULT_HEADER =
    'task,activity,activity_type,timestamp,resource,summary,comments_si,comments_fi,worklog_si,worklog_fi,issue_type,ta0,ta1,ta2,ta3';
  const looksLikeHeader = (line) => {
    if (!line || typeof line !== 'string') return false;
    if (!line.includes(',')) return false;
    if (line.includes('^')) return false; // headers are comma-separated, data rows are caret-separated
    const parts = line.split(',').map((p) => p.trim());
    if (parts.length < 2) return false;
    // Accept simple identifier-like column names (covers both event log + task attributes headers)
    return parts.every((p) => /^[A-Za-z0-9_]+$/.test(p));
  };
  let headerLine = tempRows[0];
  let rows = tempRows.slice();
  if (!looksLikeHeader(headerLine)) {
    // Legacy logs sometimes stored without header. Prepend a known header so parsing works.
    rows = [DEFAULT_HEADER, ...rows];
    headerLine = DEFAULT_HEADER;
  } else {
    // Drop the header from data rows
    rows = rows.slice(1);
  }
  const headers = headerLine.split(',').map((h) => h.trim());
  const arrayOfObjects = rows.map((row) => {
    const values = String(row).split('^');
    const obj = {};
    headers.forEach((header, index) => {
      obj[header] = values[index];
    });
    return obj;
  });
  return arrayOfObjects;
}
export function convertToCSV(objArray) {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
  let str = '';
  for (let i = 0; i < array.length; i++) {
    let line = '';
    for (const index in array[i]) {
      if (line !== '') line += '^';
      line += array[i][index];
    }
    str += line + '\r\n';
  }
  return str;
}
export function getPreviousWeeks(givenDate, numWeeks) {
  const date = new Date(givenDate);
  const weeks = [];
  // Start from the beginning of the week containing the given date (Monday)
  const currentDayOfWeek = date.getDay();
  const mondayOffset = currentDayOfWeek === 0 ? -6 : -(currentDayOfWeek - 1);
  const currentWeekStart = new Date(date);
  currentWeekStart.setDate(date.getDate() + mondayOffset);
  currentWeekStart.setHours(0, 0, 0, 0);
  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
  currentWeekEnd.setHours(23, 59, 59, 999);
  weeks.push({ start: new Date(currentWeekStart), end: new Date(currentWeekEnd) });
  for (let i = 1; i <= numWeeks; i++) {
    const weekStart = new Date(currentWeekStart);
    weekStart.setDate(currentWeekStart.getDate() - 7 * i);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    weeks.push({ start: new Date(weekStart), end: new Date(weekEnd) });
  }
  return weeks.reverse();
}
export function getPreviousMonths(givenDate, numMonths) {
  const date = new Date(givenDate);
  const months = [];
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();
  for (let i = 0; i < numMonths; i++) {
    let targetMonth = currentMonth - i;
    let targetYear = currentYear;
    while (targetMonth < 0) {
      targetMonth += 12;
      targetYear -= 1;
    }
    const monthStart = new Date(targetYear, targetMonth, 1);
    monthStart.setHours(0, 0, 0, 0);
    const monthEnd = new Date(targetYear, targetMonth + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);
    months.push({ start: new Date(monthStart), end: new Date(monthEnd) });
  }
  return months.reverse();
}
export function formattedDate(dateStr) {
  const date = new Date(dateStr);
  return (
    date.getFullYear() +
    '-' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getDate()).padStart(2, '0') +
    ' ' +
    date.toTimeString().slice(0, 8)
  );
}
export function randomBetween(min, max) {
  const random = Math.random() * (max - min) + min;
  return Math.round(random * 10) / 10;
}
export function formatDate(date) {
  let year = date.getFullYear();
  let month = date.getMonth() + 1;
  let day = date.getDate();
  if (day < 10) day = '0' + day;
  if (month < 10) month = '0' + month;
  return day + '-' + month + '-' + year;
}
export function getStandardDeviation(array) {
  const n = array.length;
  if (n === 0) return 0;
  const mean = array.reduce((a, b) => a + b, 0) / n;
  const variance = array.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b, 0) / n;
  return Math.sqrt(variance);
}
export function getMedianDuration(array) {
  const sorted = array.slice().sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length === 0) return 0;
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
}